import React from 'react';
import { useOpportunities } from '../contexts/OpportunitiesProvider';
import { useNavigation } from '../contexts/NavigationProvider';
import OpportunityCard from './PostCard';
import FilterBar from './FilterBar';
import type { User, Band, SearchResult } from '../types';

const Feed: React.FC = () => {
  const { 
    displayItems, 
    filters, 
    handleManualFilterChange,
    hasMore,
    loadMoreOpportunities,
    isLoadingMore
  } = useOpportunities();
  const { searchTerm } = useNavigation();
  
  const renderItem = (item: SearchResult) => {
    switch (item.resultType) {
        case 'opportunity':
            return <OpportunityCard key={`opp-${item.id}`} opportunity={item} />;
        case 'user':
            return <UserCard key={`user-${item.handle}`} user={item} />;
        case 'band':
            return <BandCard key={`band-${item.id}`} band={item} />;
        default:
            return null;
    }
  };

  const showLoadMore = hasMore;

  return (
    <div className="space-y-6">
      <FilterBar 
        filters={filters} 
        onFilterChange={handleManualFilterChange} 
      />
      
      {searchTerm.trim() && (
        <div className="bg-brand-surface rounded-lg p-4">
            <h3 className="font-bold text-white">
                Search Results for "{searchTerm}" ({displayItems.length})
            </h3>
        </div>
      )}

      {displayItems.length > 0 ? (
        <div className="space-y-4">
          {displayItems.map(item => renderItem(item))}
          
          {showLoadMore && (
             <button 
                onClick={loadMoreOpportunities}
                disabled={isLoadingMore}
                className="w-full py-3 bg-gray-800 text-brand-muted font-semibold rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
             >
                {isLoadingMore ? 'Loading...' : 'Load More Opportunities'}
             </button>
          )}
        </div>
      ) : (
        <div className="bg-brand-surface rounded-lg p-8 text-center text-brand-muted">
          <h3 className="text-lg font-semibold text-white">
            {searchTerm.trim() ? 'No results found' : 'No opportunities found'}
          </h3>
          <p className="mt-2">
            {searchTerm.trim() 
                ? `Your search for "${searchTerm}" did not match any users, bands, or opportunities.` 
                : 'Try adjusting your filters or check back later for new opportunities.'
            }
          </p>
        </div>
      )}
    </div>
  );
};

// --- Reusable Cards ---
const BandCard: React.FC<{ band: Band }> = ({ band }) => {
    const { handleViewBand } = useNavigation();
    return (
        <div onClick={() => handleViewBand(band)} className="flex items-center space-x-4 bg-brand-surface p-3 rounded-lg hover:bg-gray-700/50 cursor-pointer transition-colors">
            <img src={band.avatarUrl} alt={band.name} className="h-12 w-12 rounded-lg" />
            <div className="flex-1 overflow-hidden">
                <p className="font-bold text-white truncate">{band.name}</p>
                <p className="text-sm text-brand-muted truncate">{band.city}</p>
                <p className="text-xs text-gray-400 truncate mt-1">{band.members.length} member(s)</p>
            </div>
        </div>
    );
};

const UserCard: React.FC<{ user: User }> = ({ user }) => {
    const { handleViewProfile } = useNavigation();
    return (
        <div onClick={() => handleViewProfile(user)} className="flex items-center space-x-4 bg-brand-surface p-3 rounded-lg hover:bg-gray-700/50 cursor-pointer transition-colors">
            <img src={user.avatarUrl} alt={user.name} className="h-12 w-12 rounded-full" />
            <div className="flex-1 overflow-hidden">
                <p className="font-bold text-white truncate">{user.name}</p>
                <p className="text-sm text-brand-muted truncate">@{user.handle}</p>
                <p className="text-xs text-gray-400 truncate mt-1">{user.title}</p>
            </div>
        </div>
    );
};

export default Feed;